import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

/**
 * Restyled 22 August 2026 on TailAdmin's button (MIT — `shared/tailadmin/LICENSE.md`), keeping this
 * file's API rather than the template's: theirs accepts `onClick` and nothing else, so every
 * `type="submit"` and every `aria-*` in the panel would have had to be worked around.
 *
 * ⚠️ **`hover:opacity-90` is gone.** Fading a control on hover dims its label along with its
 * background — the one moment a button most needs to be readable is while the pointer is on it.
 * A hover state should be a different colour, not a weaker one.
 *
 * ⚠️ **`ghost` gained a real border and a fill on hover.** It was a grey box that changed its border
 * colour, which on a dark ground is a change most people do not see at all; half the controls in the
 * panel are this variant.
 *
 * ⚠️ Every colour resolves through a theme variable (§5.7) — `brand`, `gray` and `error` are the
 * template's names bound to ours in `app/styles.css`, not Tailwind's palettes. A literal hex here
 * would survive a theme activation in the dashboard and quietly become the one element ignoring it.
 *
 * Spacing uses logical utilities only (`px-`, `gap-`), never `ml-`/`pl-`: §23.10 makes the panel
 * flip between RTL and LTR at runtime (§5.6).
 */
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600 disabled:bg-brand-300',
  ghost: 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-100',
  danger: 'bg-error-50 text-error-500 ring-1 ring-inset ring-error-300 hover:bg-error-100',
};

const SIZES: Record<Size, string> = {
  sm: 'px-3 py-2 text-theme-xs',
  md: 'px-4 py-2.5 text-theme-sm',
};

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
};

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}: Props) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${SIZES[size]} ${VARIANTS[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
